import React, { useMemo, useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Download, ArrowUp, ArrowDown } from 'lucide-react';
import { InfoTip } from './shared/FormKit';
import { REPORT_GRADES, REPORT_BAND_OF, reportBandOf } from '../utils/staffGrades';
import { exportSheetsToExcel } from '../utils/excelExport';
import { inPeriod } from '../utils/trainingStats';
import { fetchJSON } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { exportExcelBtnCls } from './shared/ExportExcelButton';

/**
 * Official Training Report — the external-reporting block, in the format of
 * the firm's training-hours working paper (參考資料/sample_data/
 * "8. Training hours_Aug 25.xlsx", Dashboard sheet).
 *
 * Every attendance/CPD row carries `grade` — the band the
 * staff_designation option holds in Dropdown Lists. No hard-coded mapping
 * table remains: HR re-bands a designation there and this report follows on
 * the next refresh. 'Other' (non-audit designations, interns) stays out of the
 * report, mirroring the paper's "Audit Professional Staff" scope.
 *
 * Beyond the per-grade cards the report shows, when the period is bounded:
 *  - Year-on-year comparison — the same window shifted back one year,
 *    per grade and in the summary blocks (the old system faked this with
 *    hard-coded sample data; this is the real-data version).
 *  - Average headcount per grade (/api/ld/grade-headcount) and hours per
 *    headcount — the paper's denominator, which counts everyone employed in
 *    the period, not just people who attended something.
 */

// Reporting order = the paper's five columns. 'Other' is excluded. The paper
// has no Director column: Director counts under Manager here
// (REPORT_BAND_OF in src/utils/staffGrades.js).
export const AUDIT_GRADES = REPORT_GRADES;

// Column → the grades folded into it, for the column's hover note.
const FOLDED_INTO = Object.entries(REPORT_BAND_OF).reduce((acc, [grade, band]) => {
  (acc[band] = acc[band] || []).push(grade);
  return acc;
}, {});
const bandNoteOf = (band) => (FOLDED_INTO[band]
  ? `Includes ${FOLDED_INTO[band].join(', ')} — the report keeps the paper's five columns, so ${FOLDED_INTO[band].map((g) => `${g}s`).join(' and ')} are counted here.`
  : null);

// Headcount by grade → by report column.
const bandHeadcount = (byGrade) => Object.entries(byGrade || {}).reduce((acc, [grade, n]) => {
  const band = reportBandOf(grade);
  acc[band] = (acc[band] || 0) + n;
  return acc;
}, {});

const formatHours = (hours) => `${Math.round(hours)}h`;
const pluralGrade = (grade) => `${grade}s`;

// '2026-08-12' → '2025-08-12'; 29 Feb clamps to 28 Feb.
const shiftYearBack = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  const day = m === '02' && d === '29' ? '28' : d;
  return `${Number(y) - 1}-${m}-${day}`;
};

// Per-grade aggregation over one period. Grade comes from the row (API join);
// rows without one band as 'Other' and stay out of the audit scope.
// Period membership is shared with the dashboard aggregation
// (trainingStats.inPeriod — end date covers its whole day), so both blocks on
// the Training Hours page agree on which rows a bounded period includes.
// The bucketing (single internalHours vs mandatory/non-mandatory) and the
// grade attribution ('Other' upgrade on later rows) are this report's own
// spec and intentionally differ from computeTrainingStats.
const computeStats = (attendance, cpdRecords, startDate, endDate) => {
  const byPerson = {};
  const touch = (email, name, position, grade) => {
    if (!byPerson[email]) {
      byPerson[email] = {
        name: name || email.split('@')[0],
        email,
        position: position || null,
        grade: reportBandOf(grade) || 'Other',
        internalHours: 0,
        externalHours: 0,
        totalHours: 0,
      };
    } else {
      byPerson[email].name = byPerson[email].name || name;
      byPerson[email].position = byPerson[email].position || position;
      if (byPerson[email].grade === 'Other' && grade) byPerson[email].grade = reportBandOf(grade);
    }
    return byPerson[email];
  };

  // A trainer's row is not an attendance: 0 training hours, so it would
  // report a 0-hour participant in their grade and lower every average.
  // Teaching hours live in the Trainer Hours report, not in this one.
  attendance.forEach((record) => {
    if (record.isTrainer) return;
    if (!record.email || !inPeriod(record.date, startDate, endDate)) return;
    const p = touch(record.email, record.name, record.position, record.grade);
    const hours = record.trainingHours || 0;
    p.internalHours += hours;
    p.totalHours += hours;
  });

  // CPD rows are dated by the certificate, not a session date.
  cpdRecords.forEach((record) => {
    if (!record.participantEmail || !inPeriod(record.certificateDate, startDate, endDate)) return;
    const p = touch(record.participantEmail, record.participantName, record.position, record.grade);
    const hours = record.hours || 0;
    p.externalHours += hours;
    p.totalHours += hours;
  });

  const peopleArray = Object.values(byPerson);
  // Participants whose designation maps to no audit grade fall to 'Other' and
  // are outside this report's scope.
  const eligible = peopleArray.filter((p) => AUDIT_GRADES.includes(p.grade));

  const totalInternalHours = eligible.reduce((s, p) => s + p.internalHours, 0);
  const totalExternalHours = eligible.reduce((s, p) => s + p.externalHours, 0);
  const totalHours = totalInternalHours + totalExternalHours;

  const grades = AUDIT_GRADES.map((grade) => {
    const people = eligible.filter((p) => p.grade === grade);
    const internalHours = people.reduce((s, p) => s + p.internalHours, 0);
    const externalHours = people.reduce((s, p) => s + p.externalHours, 0);
    const gradeTotal = internalHours + externalHours;
    return {
      grade,
      count: people.length,
      internalHours,
      externalHours,
      totalHours: gradeTotal,
      avgHours: people.length > 0 ? gradeTotal / people.length : 0,
    };
  });

  const peopleByGrade = {};
  eligible.forEach((p) => {
    (peopleByGrade[p.grade] = peopleByGrade[p.grade] || []).push(p);
  });

  return {
    totalInternalHours,
    totalExternalHours,
    totalHours,
    avgHoursPerPerson: eligible.length > 0 ? totalHours / eligible.length : 0,
    uniquePeople: eligible.length,
    grades,
    peopleByGrade,
  };
};

const OfficialTrainingReport = ({ attendance, cpdRecords, startDate, endDate }) => {
  const { hasPermission } = useAuth();
  const [expandedGrades, setExpandedGrades] = useState(new Set());
  // {current: {grade: avg}, previous: {grade: avg}} — null until loaded.
  const [headcount, setHeadcount] = useState(null);

  const bounded = Boolean(startDate && endDate);
  const prevStart = bounded ? shiftYearBack(startDate) : null;
  const prevEnd = bounded ? shiftYearBack(endDate) : null;

  const currentPeriodStats = useMemo(
    () => computeStats(attendance, cpdRecords, startDate, endDate),
    [attendance, cpdRecords, startDate, endDate]
  );
  const previousPeriodStats = useMemo(
    () => (bounded ? computeStats(attendance, cpdRecords, prevStart, prevEnd) : null),
    [attendance, cpdRecords, bounded, prevStart, prevEnd]
  );

  // Working-paper denominator, both periods. An unbounded period has no
  // defined headcount window — the rows simply don't render then.
  useEffect(() => {
    if (!bounded) {
      setHeadcount(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      fetchJSON(`/api/ld/grade-headcount?start=${startDate}&end=${endDate}`),
      fetchJSON(`/api/ld/grade-headcount?start=${prevStart}&end=${prevEnd}`),
    ])
      .then(([cur, prev]) => {
        if (cancelled) return;
        setHeadcount({ current: bandHeadcount(cur.data.byGrade), previous: bandHeadcount(prev.data.byGrade) });
      })
      .catch((err) => {
        console.error('Error fetching grade headcount:', err);
        if (!cancelled) setHeadcount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [bounded, startDate, endDate, prevStart, prevEnd]);

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  const handleExport = () => {
    const period = `${startDate || 'All time'} to ${endDate || 'Present'}`;
    const summary = currentPeriodStats.grades.map((pos) => {
      const avgHead = headcount?.current?.[pos.grade];
      const prevPos = previousPeriodStats?.grades.find((g) => g.grade === pos.grade);
      const row = {
        'Reporting Period': period,
        Grade: pluralGrade(pos.grade),
        Staff: pos.count,
        'Total Hours': Number(pos.totalHours.toFixed(1)),
        'Internal Hours': Number(pos.internalHours.toFixed(1)),
        'External Hours': Number(pos.externalHours.toFixed(1)),
        'Avg Hours per Participant': Number(pos.avgHours.toFixed(1)),
      };
      if (avgHead !== undefined) {
        // The paper's pair: average employed headcount and hours over it.
        row['Average Headcount'] = Number(avgHead.toFixed(2));
        row['Average Training Hours'] = avgHead > 0 ? Number((pos.totalHours / avgHead).toFixed(1)) : 0;
      }
      if (prevPos) {
        row['Previous Period Hours'] = Number(prevPos.totalHours.toFixed(1));
        const change = calculateChange(pos.totalHours, prevPos.totalHours);
        row['Change %'] = change === null ? null : Number(change.toFixed(1));
      }
      return row;
    });
    const detail = Object.entries(currentPeriodStats.peopleByGrade).flatMap(([grade, people]) =>
      people
        .slice()
        .sort((a, b) => b.totalHours - a.totalHours)
        .map((person) => ({
          'Reporting Period': period,
          Grade: pluralGrade(grade),
          Name: person.name,
          Email: person.email,
          Designation: person.position || 'Unknown',
          'Total Hours': Number(person.totalHours.toFixed(1)),
          'Internal Hours': Number(person.internalHours.toFixed(1)),
          'External Hours': Number(person.externalHours.toFixed(1)),
        }))
    );
    exportSheetsToExcel(
      [{ name: 'By Grade', rows: summary }, { name: 'Staff Detail', rows: detail }],
      'Official_Training_Report.xlsx'
    );
  };

  const getColorClass = (grade) => {
    const colorClasses = {
      'Partner': 'from-gray-700 to-gray-800 border-gray-600',
      'Manager': 'from-blue-600 to-blue-700 border-blue-500',
      'Assistant Manager': 'from-gray-600 to-gray-700 border-gray-500',
      Senior: 'from-yellow-500 to-yellow-600 border-yellow-400',
      Assistant: 'from-purple-600 to-purple-700 border-purple-500',
    };
    return colorClasses[grade] || 'from-gray-500 to-gray-600 border-gray-400';
  };

  const totalChange = previousPeriodStats
    ? calculateChange(currentPeriodStats.totalHours, previousPeriodStats.totalHours)
    : null;

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg shadow-md border-2 border-indigo-300 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Official Training Report</h3>
            <p className="text-sm text-gray-600">Aggregated by staff grade for external reporting</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium text-gray-700">Reporting Period</p>
            <p className="text-lg font-bold text-indigo-700">
              {startDate || 'All time'} {endDate ? `— ${endDate}` : '— Present'}
            </p>
          </div>
          {hasPermission('ld:view') && (
            <button
              onClick={handleExport}
              disabled={currentPeriodStats.uniquePeople === 0}
              className={exportExcelBtnCls}
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          )}
        </div>
      </div>

      {/* Grade cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {currentPeriodStats.grades.map((pos) => {
          const colorClass = getColorClass(pos.grade);
          const isExpanded = expandedGrades.has(pos.grade);
          const staffMembers = currentPeriodStats.peopleByGrade[pos.grade] || [];
          const avgHead = headcount?.current?.[pos.grade];
          const prevPos = previousPeriodStats?.grades.find((g) => g.grade === pos.grade);
          const hoursChange = prevPos ? calculateChange(pos.totalHours, prevPos.totalHours) : null;

          const toggleExpanded = () => {
            const next = new Set(expandedGrades);
            if (isExpanded) next.delete(pos.grade);
            else next.add(pos.grade);
            setExpandedGrades(next);
          };

          return (
            <div key={pos.grade} className="bg-white rounded-lg border-2 border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              {/* The column note (InfoTip) sits beside the toggle, not inside
                  it, and the card does not clip — so its bubble can show. */}
              <div className={`relative rounded-t-md bg-gradient-to-r ${colorClass}`}>
                <button
                  onClick={toggleExpanded}
                  className="w-full p-3 flex items-center justify-between hover:opacity-90 transition-opacity"
                >
                  <h4 className="text-white font-bold text-center text-sm flex-1">{pluralGrade(pos.grade)}</h4>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-white" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-white" />
                  )}
                </button>
                {bandNoteOf(pos.grade) && (
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 flex">
                    <InfoTip text={bandNoteOf(pos.grade)} />
                  </span>
                )}
              </div>
              <div className="p-4 space-y-2">
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900">{formatHours(pos.totalHours)}</p>
                  <p className="text-xs text-gray-500">Total Hours</p>
                  {hoursChange !== null && (
                    <p className={`text-xs font-semibold mt-1 ${hoursChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {hoursChange >= 0
                        ? <ArrowUp className="w-3.5 h-3.5 inline -mt-0.5" />
                        : <ArrowDown className="w-3.5 h-3.5 inline -mt-0.5" />}
                      {' '}{Math.abs(hoursChange).toFixed(1)}% vs last year
                    </p>
                  )}
                </div>
                <div className="pt-2 border-t border-gray-200 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Staff:</span>
                    <span className="font-semibold text-gray-900">{pos.count}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Avg/Person:</span>
                    <span className="font-semibold text-gray-900">{formatHours(pos.avgHours)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Internal:</span>
                    <span className="font-medium text-blue-700">{formatHours(pos.internalHours)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">External:</span>
                    <span className="font-medium text-green-700">{formatHours(pos.externalHours)}</span>
                  </div>
                  {avgHead !== undefined && (
                    <>
                      <div className="flex justify-between text-xs pt-1 border-t border-gray-100">
                        <span className="text-gray-600">Avg Headcount:</span>
                        <span className="font-medium text-gray-900">{avgHead.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Hours/Headcount:</span>
                        <span className="font-medium text-gray-900">
                          {avgHead > 0 ? (pos.totalHours / avgHead).toFixed(1) : '—'}h
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* Expandable staff list */}
                {isExpanded && staffMembers.length > 0 && (
                  <div className="mt-3 pt-3 border-t-2 border-gray-300">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Staff Members:</p>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {staffMembers
                        .slice()
                        .sort((a, b) => b.totalHours - a.totalHours)
                        .map((person, idx) => (
                          <div key={idx} className="bg-gray-50 rounded p-2 text-xs">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-medium text-gray-900 flex-1">{person.name}</span>
                              <span className="font-bold text-gray-900">{formatHours(person.totalHours)}</span>
                            </div>
                            <div className="text-gray-600 space-y-0.5">
                              <div className="flex justify-between">
                                <span>Designation:</span>
                                <span className="font-medium">{person.position || 'Unknown'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Internal:</span>
                                <span className="text-blue-700">{formatHours(person.internalHours)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>External:</span>
                                <span className="text-green-700">{formatHours(person.externalHours)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Training Hours Comparison — the old system's two-period block, now on
          real data: the selected window vs the same window a year earlier. */}
      {previousPeriodStats && (
        <div className="mt-6 pt-4 border-t-2 border-indigo-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Training Hours Comparison</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-green-800 mb-3">
                {startDate} — {endDate}
              </p>
              <div className="space-y-1.5">
                {currentPeriodStats.grades.map((g) => (
                  <div key={g.grade} className="flex justify-between text-sm">
                    <span className="text-gray-700">{pluralGrade(g.grade)}</span>
                    <span className="font-medium text-gray-900">{formatHours(g.totalHours)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm pt-2 border-t border-green-200 font-bold">
                  <span className="text-gray-900">Subtotal</span>
                  <span className="text-gray-900">{formatHours(currentPeriodStats.totalHours)}</span>
                </div>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-800 mb-3">
                {prevStart} — {prevEnd}
              </p>
              <div className="space-y-1.5">
                {previousPeriodStats.grades.map((g) => (
                  <div key={g.grade} className="flex justify-between text-sm">
                    <span className="text-gray-700">{pluralGrade(g.grade)}</span>
                    <span className="font-medium text-gray-900">{formatHours(g.totalHours)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-sm pt-2 border-t border-amber-200 font-bold">
                  <span className="text-gray-900">Subtotal</span>
                  <span className="text-gray-900">{formatHours(previousPeriodStats.totalHours)}</span>
                </div>
              </div>
            </div>
          </div>
          {totalChange !== null && (
            <p className="text-xs text-gray-600 mt-3">
              <strong>Note:</strong> Current period shows {Math.abs(totalChange).toFixed(1)}%
              {totalChange >= 0 ? ' increase' : ' decrease'} compared to the same period last year
              ({totalChange >= 0 ? '+' : '−'}{Math.abs(currentPeriodStats.totalHours - previousPeriodStats.totalHours).toFixed(1)} hours).
            </p>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="mt-6 pt-4 border-t-2 border-indigo-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Total Participants</p>
            <p className="text-2xl font-bold text-indigo-900">{currentPeriodStats.uniquePeople}</p>
            <p className="text-xs text-gray-600 mt-1">Across audit staff grades</p>
            {previousPeriodStats && calculateChange(currentPeriodStats.uniquePeople, previousPeriodStats.uniquePeople) !== null && (
              <p className={`text-xs font-semibold mt-1 ${
                calculateChange(currentPeriodStats.uniquePeople, previousPeriodStats.uniquePeople) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                vs {previousPeriodStats.uniquePeople} last year ({calculateChange(currentPeriodStats.uniquePeople, previousPeriodStats.uniquePeople)?.toFixed(1)}%)
              </p>
            )}
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Total Training Hours</p>
            <p className="text-2xl font-bold text-blue-900">{formatHours(currentPeriodStats.totalHours)}</p>
            <p className="text-xs text-gray-600 mt-1">Combined internal + external</p>
            {totalChange !== null && (
              <p className={`text-xs font-semibold mt-1 ${totalChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                vs {formatHours(previousPeriodStats.totalHours)} last year ({totalChange.toFixed(1)}%)
              </p>
            )}
          </div>

          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Average per Person</p>
            <p className="text-2xl font-bold text-green-900">{formatHours(currentPeriodStats.avgHoursPerPerson)}</p>
            <p className="text-xs text-gray-600 mt-1">Audit-grade average</p>
            {previousPeriodStats && calculateChange(currentPeriodStats.avgHoursPerPerson, previousPeriodStats.avgHoursPerPerson) !== null && (
              <p className={`text-xs font-semibold mt-1 ${
                calculateChange(currentPeriodStats.avgHoursPerPerson, previousPeriodStats.avgHoursPerPerson) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                vs {formatHours(previousPeriodStats.avgHoursPerPerson)} last year ({calculateChange(currentPeriodStats.avgHoursPerPerson, previousPeriodStats.avgHoursPerPerson)?.toFixed(1)}%)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Export note */}
      <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs text-blue-800">
          <strong>Note:</strong> This report bands designations into {AUDIT_GRADES.length} audit staff grades
          (maintained in Dropdown Lists → Staff Designation) for external audit compliance; the Other grade is excluded
          and Directors are counted under Managers.
        </p>
        <p className="text-xs text-blue-700 mt-1">
          <strong>Training Hours:</strong> Internal = Company training sessions (Attendance records) | External = External CPD courses (External CPD Records)
        </p>
        {headcount && (
          <p className="text-xs text-blue-700 mt-1">
            <strong>Average Headcount:</strong> monthly month-end headcount from the staff master, averaged over the period — includes staff without training records; historical months use each person's current designation.
          </p>
        )}
      </div>
    </div>
  );
};

export default OfficialTrainingReport;
